import { DateTime } from 'luxon';
import { ACTION_TYPES, RESERVATION_TYPES } from '../models/action';
import {
  activeStatus,
  archiveStatus,
  archivingReason,
  closeStatus,
  inactiveStatus,
} from '../constants/events';
import { useSeoAnalytics } from '../providers/seo-analytics';

const checkPassedWithTime = (now, endDate) => {
  for (let i = 0; i < endDate.time.length; i += 1) {
    const date = DateTime.fromFormat(
      `${endDate.end_date} ${endDate.time[i]}`,
      'yyyy-LL-dd HH:mm:ss'
    ).valueOf();
    if (now < date) {
      return false;
    }
  }
  return true;
};

export const getCancellationPolicyText = (cancellationPolicy) => {
  switch (cancellationPolicy) {
    case 'free_cancel':
      return 'бесплатная отмена и возврат средств за 72 часа до мероприятия';
    case '10_days':
      return 'отмена билетов и возврат средств: более 10 дней – 100% стоимости билета, менее 10 дней, но более 5 дней – 50%, менее 5 дней, но более 3 дней – 30%, менее 3 дней – возврат денежных средств не производится';
    default:
      return 'возврат билетов, приобретенных со скидкой, не осуществляется';
  }
};

export const isEventPassed = (action) => {
  const now = new Date().getTime();
  let eventPassed = true;

  if (!action?.dates?.length) return eventPassed;

  for (let i = 0; i < action.dates.length; i += 1) {
    const endDate = action.dates[i];
    const date = new Date(endDate.end_date).getTime();

    if (now > date && endDate.time.length) {
      eventPassed = checkPassedWithTime(now, endDate);
    } else {
      eventPassed = now > new Date(`${endDate.end_date} 23:59:59`).getTime();
    }
    if (!eventPassed) break;
  }

  return eventPassed;
};

export const MaxNumberReservationsAtATime = 20;

export const useSendMetricsFromButton = () => {
  const seoAnalytics = useSeoAnalytics();
  const onSend = (item, isRanOutOfTickets) => {
    // 'Подписка оформлена';
    if (item.status === activeStatus && item.subscription_confirmed) {
      return;
    }
    // 'Подписаться на уведомления';
    if (item.status === activeStatus && isRanOutOfTickets) {
      return;
    }
    // 'У события неактивный статус'
    if (
      item.status === archiveStatus ||
      item.status === closeStatus ||
      item.status === inactiveStatus
    ) {
      return;
    }
    if (
      item.type === ACTION_TYPES.reservation &&
      item.reservation_type === RESERVATION_TYPES.shortened
    ) {
      // 'Оставить заявку'
      seoAnalytics.dispatch({ ym: ['Click_request'] });
      return;
    }
    if (item.type === ACTION_TYPES.FREE) {
      // 'Посетить бесплатно'
      seoAnalytics.dispatch({ ym: ['Click_free'] });
      return;
    }
    if (item.type === ACTION_TYPES.reservation) {
      // 'Забронировать со скидкой || Забронировать'
      if (!item.discount) {
        seoAnalytics.dispatch({ ym: ['Click_book'] });
        return;
      }
      seoAnalytics.dispatch({ ym: ['Click_book_sale'] });
      return;
    }
    if (item.type === ACTION_TYPES.partner_widget) {
      // Партнерский виджет
      seoAnalytics.dispatch({ ym: ['widget_buy'] });
      return;
    }
    // 'Купить со скидкой || Купить'
    if (item.type === ACTION_TYPES.certificate) {
      if (!item.discount) {
        seoAnalytics.dispatch({ ym: ['Click_buy'] });
        return;
      }
      seoAnalytics.dispatch({ ym: ['Click_buy_sale'] });
      return;
    }
    // 'Купить со скидкой || Купить' (смартбилет)
    if (item.type === ACTION_TYPES.purchase) {
      if (!item.discount) {
        seoAnalytics.dispatch({ ym: ['Click_buy_smart'] });
        return;
      }
      seoAnalytics.dispatch({ ym: ['Click_buy_sale_smart'] });
      return;
    }

    // Новый виджет от гб
    if (item.type === ACTION_TYPES.tsnext_widget) {
      seoAnalytics.dispatch({ ym: ['Click_new_biletka'] });
      return;
    }
    seoAnalytics.dispatch({ ym: ['Click_buy'] });
  };
  return {
    onSend,
  };
};

export function getBuyButtonText(
  item,
  isRanOutOfTickets = false,
  isFromV4Data = false
) {
  if (item.status === activeStatus && item.subscription_confirmed) {
    return 'Подписка оформлена';
  }
  if (item.status === activeStatus && isRanOutOfTickets) {
    return 'Подписаться на уведомления';
  }
  if (item.status === activeStatus && item.not_season) {
    return 'Подписаться на событие';
  }
  if (
    item.status === archiveStatus ||
    item.status === closeStatus ||
    item.status === inactiveStatus
  ) {
    if (item.archiving_reason) {
      if (item.archiving_reason === archivingReason.eventPassed) {
        return 'Событие состоялось';
      }
      if (item.archiving_reason === archivingReason.eventCanceled) {
        return 'Событие отменено';
      }
      if (item.archiving_reason === archivingReason.ticketsOver) {
        return 'Закончились билеты/места';
      }
    } else {
      return 'Акция завершена';
    }
  }
  if (
    item.type === ACTION_TYPES.reservation &&
    item.reservation_type === RESERVATION_TYPES.shortened
  ) {
    return 'Оставить заявку';
  }
  if (isFromV4Data && !item.has_further_dates) {
    return 'Событие состоялось';
  }
  if (!isFromV4Data && isEventPassed(item) && !item.date_unknown) {
    return 'Событие состоялось';
  }
  if (item.type === ACTION_TYPES.FREE) {
    return 'Посетить бесплатно';
  }
  if (item.type === ACTION_TYPES.reservation) {
    return `Забронировать${item.discount ? ' со скидкой' : ''}`;
  }
  if (item.type === ACTION_TYPES.partner_widget) {
    return item.purchase_button ? item.purchase_button : 'Купить';
  }
  if (item.type === ACTION_TYPES.tsnext_widget) {
    return item.purchase_button ? item.purchase_button : 'Купить';
  }
  if ([ACTION_TYPES.certificate, ACTION_TYPES.purchase].includes(item.type)) {
    return `Купить${item.discount ? ' со скидкой' : ''}`;
  }
  return 'Купить';
}

export const hasOwnPolyfill = (obj, property) => {
  if (Object.hasOwn) {
    return Object.hasOwn(obj, property);
  }
  // eslint-disable-next-line no-prototype-builtins
  return obj.hasOwnProperty(property);
};
